import React, { useCallback, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Phone, ShareNetwork, FilePdf } from 'components/shared/Icon/phosphorIcons';
import { TBlock } from 'detailsv2/__blocks';
import { useLandingV2Context } from 'detailsv2/LandingV2/context';
import { getLandingV2Config } from 'detailsv2/LandingV2/functions';
import { usePageContext } from 'detailsv2/LandingV2/Page/context';
import { TAddBlockInsideSpoilerFn, TDeleteBlockInsideSpoilerFn } from 'detailsv2/LandingV2/Page/RightSide/Block/types';
import { ILink } from 'detailsv2/LandingV2/TableOfContents/types';
import { ILandingConfig, TBlockHandlersProviderFn, TPageHandlersProviderFn } from 'detailsv2/LandingV2/types';
import i18n from 'i18n';
import { isLoginAsSelector } from 'store/auth/selectors';
import { itineraryWithoutEmptyPagesSelector } from 'store/itinerariesItem/selectors';
import {
  addLandingBlockThunk,
  editLandingBlockThunk,
  editLandingPageThunk,
  removeLandingBlockThunk,
  setProposalLandingV2Config,
  updateLandingPageHiddenValueThunk,
  addLandingBlockInsideAccordionThunk,
  editLandingBlockInsideAccordionThunk,
  removeLandingBlockInsideAccordionThunk,
} from 'store/itinerariesItem/thunks';
import { isSuperAdminSelector } from 'store/user/selectors';
import { debounce } from 'utils/debounce';
import { saveAsDOCX } from 'utils/filesDownloaders/docx';
import { saveAsPDF } from 'utils/filesDownloaders/pdf';
import { getLangingPageType } from 'utils/itineraries/functions';
import {
  removeAIBlocksFromProposal,
  getPageName,
  useItinerariesItemSaveHandler,
  useSaveSampleItemSaveHandler,
} from 'utils/itineraries/functions/builderUtils';
import { getDefaultAnchor, TAnchor } from 'utils/itineraries/functions/utils';
import { prepareProposalPages } from 'utils/itineraries/guestPortal/prepareGuestPortal';
import { ILanding, IPage, IProposal } from 'utils/itineraryDetails/types';
import { handleNestedAccordion } from 'utils/nestedAccordions';

import { addDataToProposalBlocks, useProposalDifferenceHandler } from '../../../shared/functions';
import { useReplaceCoverHandler } from '../../../shared/ItinerariesItemLanding/Builder/functions';
import { IBuilderPage } from '../../../shared/ItinerariesItemLanding/Builder/types';
import { useContentPagePreview } from '../../../shared/ItinerariesItemLanding/functions';
import { IIcon } from '../../../shared/ItinerariesItemLanding/LandingActions/types';

export function useLandingBuilderV2() {
  const { withContentPagePreview, pagePreviewId: originalPagePreviewId } = useContentPagePreview();
  const dispatch = useDispatch();
  const isSuper = useSelector(isSuperAdminSelector);
  const proposal: IProposal = useSelector(itineraryWithoutEmptyPagesSelector);
  const preparedProposal: IProposal = useMemo(() => prepareProposal(proposal), [proposal]);
  const defaultAnchor = useMemo(
    () => getDefaultAnchor(preparedProposal, originalPagePreviewId),
    [preparedProposal, originalPagePreviewId],
  );
  const { links, activePageId, setActivePageId } = useTableOfContentsProps({
    proposal: preparedProposal,
    defaultAnchor,
  });
  const isOverriding = useSelector(isLoginAsSelector);
  const isCustomCSSV2Enabled = isSuper || isOverriding || localStorage.getItem('__spCustomCSS') === '1';
  const landingConfig = getLandingV2Config({ ...preparedProposal.landingConfig, settings: preparedProposal.settings });
  const handleLandingConfigChange = useCallback(
    (landingConfig: ILandingConfig) => {
      dispatch(setProposalLandingV2Config(landingConfig));
    },
    [dispatch],
  );

  const handleSetActivePageId = useCallback(
    (pageId: number) => {
      setActivePageId(pageId);
    },
    [setActivePageId],
  );

  return {
    proposal: preparedProposal,
    links,
    activePageId,
    defaultAnchor,
    isCustomCSSV2Enabled,
    landingConfig,
    handleSetActivePageId,
    handleLandingConfigChange,
    withContentPagePreview,
    originalPagePreviewId,
  };
}

export function useTableOfContentsProps({ proposal, defaultAnchor }: { proposal: IProposal; defaultAnchor: TAnchor }) {
  const dispatch = useDispatch();
  const [activePageId, setActivePageId] = useState(defaultAnchor.pageId || proposal.landing.pages[0].id);

  const links: ILink[] = useMemo(
    () =>
      proposal.landing.pages.map((p, i) => ({
        id: p.id,
        name: getPageName(p),
        navBarTitle: p.navBarTitle,
        builderTitle: p.builderTitle,
        isHidden: p.hidden || p.empty,
        canBeVisible: !p.empty,
        onClick: (id) => setActivePageId(id),
        onHide: () => {
          if (!p.empty) {
            dispatch(updateLandingPageHiddenValueThunk({ pageIndex: i, hidden: !p.hidden }));
          }
        },
      })),
    [dispatch, proposal.landing.pages, i18n.version],
  );

  return { links, activePageId, setActivePageId };
}

export const iconsConfig: IIcon[] = [
  {
    type: 'share',
    icon: <ShareNetwork data-tip="" data-for="share" />,
    onClick: () => {},
    hint: i18n.t('proposal_content.copy_link'),
    isDisabled: true,
  },
  {
    type: 'pdf',
    icon: <FilePdf data-tip="" data-for="pdf" />,
    onClick: () => {},
    hint: i18n.t('proposal_content.file.download_as_pdf'),
    isDisabled: true,
  },
  {
    type: 'phone',
    icon: <Phone data-tip="" data-for="phone" />,
    onClick: () => {},
    hint: i18n.t('proposal_content.contact.travel_consultant'),
    isDisabled: true,
  },
];

export const PageHandlersProvider: TPageHandlersProviderFn = ({ pageIndex, children }) => {
  const dispatch = useDispatch();
  const { data } = useLandingV2Context();
  const changeCoverImage = useReplaceCoverHandler({
    pageIndex,
    page: data as unknown as IBuilderPage,
    landingData: data.landing.pages[pageIndex],
    landingPageType: getLangingPageType(data.landing.pages[pageIndex]),
  });

  const changeContent = useCallback(
    (data: Partial<IPage>) => {
      dispatch(editLandingPageThunk({ pageIndex, newObject: data }));
    },
    [dispatch, pageIndex],
  );

  return children({ changeCoverImage, changeContent });
};

export const BlockHandlersProvider: TBlockHandlersProviderFn = ({ children, index: blockIndex }) => {
  const dispatch = useDispatch();
  const { index: pageIndex } = usePageContext();

  const onAdd = useCallback(
    (newObject: Partial<TBlock> | Partial<TBlock>[]) => {
      dispatch(
        addLandingBlockThunk({
          pageIndex,
          blockIndex,
          newObject,
        }),
      );
    },
    [pageIndex, blockIndex, dispatch],
  );

  const onUpdate = useCallback(
    debounce((newObject: Partial<TBlock>) => {
      dispatch(
        editLandingBlockThunk({
          pageIndex,
          blockIndex,
          newObject,
        }),
      );
    }, 300),
    [pageIndex, blockIndex, dispatch],
  );

  const onDelete = useCallback(() => {
    dispatch(
      removeLandingBlockThunk({
        pageIndex,
        blockIndex,
      }),
    );
  }, [pageIndex, blockIndex, dispatch]);

  const onAddBlockInsideSpoiler = useCallback<TAddBlockInsideSpoilerFn>(
    ({ blockIndex, spoilerIndex }) =>
      (spoilerBlockIndex, insertPosition) =>
      (newObject) => {
        const handledData = handleNestedAccordion(newObject);
        if (!handledData) return;

        dispatch(
          addLandingBlockInsideAccordionThunk({
            pageIndex,
            blockIndex,
            spoilerIndex,
            spoilerBlockIndex,
            newObject: handledData,
            insertPosition,
          }),
        );
      },
    [],
  );

  const onUpdateBlockInsideSpoiler = useCallback<TAddBlockInsideSpoilerFn>(
    ({ blockIndex, spoilerIndex }) =>
      (spoilerBlockIndex) =>
      (newObject) => {
        dispatch(
          editLandingBlockInsideAccordionThunk({
            pageIndex,
            blockIndex,
            spoilerIndex,
            spoilerBlockIndex,
            newObject,
          }),
        );
      },
    [],
  );

  const onDeleteBlockInsideSpoiler = useCallback<TDeleteBlockInsideSpoilerFn>(
    ({ blockIndex, spoilerIndex }) =>
      (spoilerBlockIndex: number) =>
      () =>
        dispatch(
          removeLandingBlockInsideAccordionThunk({
            pageIndex,
            blockIndex,
            spoilerIndex,
            spoilerBlockIndex,
          }),
        ),
    [],
  );

  return children({
    onAdd,
    onUpdate,
    onDelete,
    onAddBlockInsideSpoiler,
    onUpdateBlockInsideSpoiler,
    onDeleteBlockInsideSpoiler,
  });
};

export const useActionButtons = () => {
  const itineraryItem = useSelector(itineraryWithoutEmptyPagesSelector);
  const pages = itineraryItem?.landing?.pages || [];
  const { updatedPages, setNewPages } = useProposalDifferenceHandler(pages);
  const [submitting, setSubmitting] = useState(false);
  const handleSave = useItinerariesItemSaveHandler({
    onPrepareSaveData: (itinerary: ILanding) => {
      return removeAIBlocksFromProposal(itinerary);
    },
    onAfterSave: (newItinerary: IProposal) => {
      // FIXME: fix type assertion
      setNewPages(newItinerary.landing.pages as never[]);
    },
  });
  const onSave = async () => {
    try {
      setSubmitting(true);
      await handleSave({
        pages: updatedPages,
        customStyles: itineraryItem?.customStyles,
        customFonts: itineraryItem?.customFonts,
        landingConfig: itineraryItem?.landingConfig,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onSavePdf = () => {
    onSave().then(() =>
      saveAsPDF({
        item: itineraryItem,
        itinerariesItemType: itineraryItem.type,
      } as any),
    );
  };

  const onSaveDOCX = (themeId: string, etag: string) => {
    onSave().then(() => saveAsDOCX({ item: itineraryItem, itinerariesItemType: itineraryItem.type, themeId, etag }));
  };

  const saveAsSample = useSaveSampleItemSaveHandler();

  const handleSaveWithoutSnackbar = useItinerariesItemSaveHandler({
    showSuccessSnackbar: false,
    onAfterSave: (newItinerary: IProposal) => {
      // FIXME: fix type assertion
      setNewPages(newItinerary.landing.pages as never[]);
    },
  });
  const handleSaveAsSample = async () => {
    try {
      setSubmitting(true);
      await handleSaveWithoutSnackbar({ pages: updatedPages });
      await saveAsSample(itineraryItem?.id);
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, onSave, onSavePdf, onSaveDOCX, handleSaveAsSample };
};

const prepareProposal = (proposal: IProposal) => {
  return addDataToProposalBlocks(prepareProposalPages(proposal, { hideTravelWalletIfEmpty: true }));
};
